/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import FooterPageLayout from '../../components/layouts/FooterPageLayout';
import { Sparkles, Users, TrendingUp, Star, Gift, Zap, Globe, Video, Calendar, Mail, X } from 'lucide-react';

const Host = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    eventType: '',
    city: '',
    preferredFormat: 'in-person',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email to aaron@pebbletheapp.com and user
      const response = await fetch('/api/send-pebble-party-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          organization: '',
          eventType: '',
          city: '',
          preferredFormat: 'in-person',
          message: ''
        });
        setTimeout(() => {
          setShowForm(false);
          setSubmitSuccess(false);
        }, 3000);
      } else {
        alert('There was an error submitting your request. Please try again or contact support@pebbletheapp.com');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact support@pebbletheapp.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FooterPageLayout>
      <div className="min-h-screen bg-gradient-to-b from-grey-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#B22A2B] to-[#8a2122] text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Sparkles className="w-12 h-12" />
              </div>
              <h1 className="text-5xl font-bold mb-4">Host a Pebble Party</h1>
              <p className="text-xl max-w-3xl mx-auto opacity-90">
                Get intimate with the product, connect with experts, and unlock exclusive insights to supercharge your events
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* What is a Pebble Party */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-grey-900 mb-6 text-center">What's a Pebble Party?</h2>
            <p className="text-lg text-grey-700 text-center max-w-4xl mx-auto leading-relaxed">
              A Pebble Party is an exclusive, interactive session where our team comes to your city—or joins you virtually—to provide hands-on product training, answer your questions, share proven strategies for increasing event revenue and attendance, and connect you with fellow event organizers, vendors, investors, and strategic partners.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-grey-900 mb-8 text-center">Why Host a Pebble Party?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-[#B22A2B]">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Users className="w-6 h-6 text-[#B22A2B]" />
                  </div>
                  <h3 className="font-semibold text-grey-900 ml-3 text-lg">Get Intimate with Pebble</h3>
                </div>
                <p className="text-grey-600">Hands-on product demos, deep dives into features, and personalized guidance from our team</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-[#B22A2B]">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-[#B22A2B]" />
                  </div>
                  <h3 className="font-semibold text-grey-900 ml-3 text-lg">Boost Revenue & Attendance</h3>
                </div>
                <p className="text-grey-600">Learn proven tips and strategies to increase event revenue, attendee turnout, and vendor participation</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-[#B22A2B]">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Star className="w-6 h-6 text-[#B22A2B]" />
                  </div>
                  <h3 className="font-semibold text-grey-900 ml-3 text-lg">Exclusive Case Studies</h3>
                </div>
                <p className="text-grey-600">Access real success stories and best practices from top-performing events on our platform</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-[#B22A2B]">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Gift className="w-6 h-6 text-[#B22A2B]" />
                  </div>
                  <h3 className="font-semibold text-grey-900 ml-3 text-lg">Early Access to Features</h3>
                </div>
                <p className="text-grey-600">Get exclusive updates, early product rollouts, and sneak peeks at upcoming features before anyone else</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-[#B22A2B]">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Zap className="w-6 h-6 text-[#B22A2B]" />
                  </div>
                  <h3 className="font-semibold text-grey-900 ml-3 text-lg">Network with Key Players</h3>
                </div>
                <p className="text-grey-600">Connect with investors, strategic partners, and industry leaders to grow your events and business</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 border-[#B22A2B]">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Calendar className="w-6 h-6 text-[#B22A2B]" />
                  </div>
                  <h3 className="font-semibold text-grey-900 ml-3 text-lg">Ask Anything</h3>
                </div>
                <p className="text-grey-600">Direct Q&A with Pebble experts—get answers to your toughest event management questions</p>
              </div>
            </div>
          </div>

          {/* Format Options */}
          <div className="bg-gradient-to-br from-grey-50 to-grey-100 rounded-xl p-8 mb-12">
            <h2 className="text-3xl font-bold text-grey-900 mb-8 text-center">We Come to You</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <Globe className="w-8 h-8 text-[#B22A2B] mr-3" />
                  <h3 className="text-xl font-semibold text-grey-900">In-Person</h3>
                </div>
                <p className="text-grey-700">
                  Our team travels to your city for an immersive, hands-on experience with your team, partners, and community
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <Video className="w-8 h-8 text-[#B22A2B] mr-3" />
                  <h3 className="text-xl font-semibold text-grey-900">Virtual</h3>
                </div>
                <p className="text-grey-700">
                  Can't meet in person? We'll host an interactive virtual session that's just as engaging and valuable
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-white rounded-xl shadow-lg p-12">
            <h2 className="text-3xl font-bold text-grey-900 mb-4">Ready to Host Your Pebble Party?</h2>
            <p className="text-lg text-grey-600 mb-8 max-w-2xl mx-auto">
              Fill out our simple form and we'll reach out to plan an unforgettable experience for you and your team
            </p>
            <button 
              onClick={() => setShowForm(true)}
              className="bg-[#B22A2B] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#8a2122] transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
            >
              Start Planning Your Pebble Party
            </button>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-grey-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-grey-900">Request a Pebble Party</h3>
              <button 
                onClick={() => setShowForm(false)}
                className="text-grey-400 hover:text-grey-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {submitSuccess ? (
              <div className="p-8 text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-grey-900 mb-2">Request Submitted!</h4>
                <p className="text-grey-600">
                  Thank you! We've sent a confirmation to your email and our team will reach out soon to plan your Pebble Party.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Organization
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                    placeholder="Your organization or event name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Event Type
                  </label>
                  <input
                    type="text"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                    placeholder="Festival, Market, Fair, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    City/Location
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                    placeholder="Where would you like to host?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Preferred Format <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="preferredFormat"
                    value={formData.preferredFormat}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                  >
                    <option value="in-person">In-Person</option>
                    <option value="virtual">Virtual</option>
                    <option value="either">Either works</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-grey-300 rounded-lg focus:ring-2 focus:ring-[#B22A2B] focus:border-transparent"
                    placeholder="Tell us more about your goals, expected attendees, or any questions you have..."
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="flex-1 px-6 py-3 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-[#B22A2B] text-white rounded-lg hover:bg-[#8a2122] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>

                <p className="text-xs text-grey-500 text-center">
                  Need immediate assistance? Email us at{' '}
                  <a href="mailto:support@pebbletheapp.com" className="text-[#B22A2B] hover:underline">
                    support@pebbletheapp.com
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </FooterPageLayout>
  );
};

export default Host;
